import React, { useEffect, useRef, useState } from 'react';
// import { useTranslation } from 'react-i18next';
import './Notification.scss';

/*
Props:
 - isOpen: boolean
 - onClose?: () => void
 - autoCloseMs?: number | null (null/undefined = no auto close)
 - iconSrc?: string
 - title?: string
 - subtitle?: string
 - isClosable?: boolean (X icon)
 - position?: 'top-start'|'top-center'|'top-end'|'bottom-start'|'bottom-center'|'bottom-end'
 - cta?: { type: 'button'|'link', text: string, onClick?: () => void, href?: string }
 - variant?: 'default'
 - onClick?: () => void (entire notification clickable when cta exists)
*/

export default function Notification({
  isOpen,
  onClose,
  autoCloseMs,
  iconSrc,
  title,
  subtitle,
  isClosable = false,
  position = 'top-center',
  cta,
  variant = 'default',
  onClick,
  closeIconSrc = '/assets/icons/iconX.svg',
  delayMs = 0,
}) {
  // const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    if (autoCloseMs && autoCloseMs > 0) {
      const id = setTimeout(() => {
        onClose && onClose();
      }, autoCloseMs);
      return () => clearTimeout(id);
    }
  }, [isOpen, autoCloseMs, onClose]);

  const clickableProps = {};
  if (cta) {
    clickableProps.onClick = (e) => {
      // avoid double trigger when clicking inner CTA
      if (e.defaultPrevented) return;
      if (cta?.type === 'button' && cta?.onClick) cta.onClick();
      if (cta?.type === 'link' && cta?.href) {
        window.location.assign(cta.href);
      }
      onClick && onClick();
    };
  }

  const [closing, setClosing] = useState(false);
  const [entered, setEntered] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setClosing(false);
      setShow(false);
      const id = setTimeout(() => {
        setEntered(false);
        setShow(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setEntered(true));
        });
      }, Math.max(0, delayMs || 0));
      return () => clearTimeout(id);
    } else {
      setEntered(false);
      setShow(false);
      setClosing(true);
      const tid = setTimeout(() => { setClosing(false); }, 200);
      return () => clearTimeout(tid);
    }
  }, [isOpen, delayMs]);

  const bannerRef = useRef(null);
  useEffect(() => {
    const applyOffset = (active) => {
      const height = active && bannerRef.current ? bannerRef.current.offsetHeight : 0;
      document.documentElement.style.setProperty('--notification-height', `${height}px`);
      const vh = (window.innerHeight - height) * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    if (show && !closing) {
      document.body.classList.add('has-top-notification');
      applyOffset(true);
    } else {
      document.body.classList.remove('has-top-notification');
      applyOffset(false);
    }
    return () => {
      document.body.classList.remove('has-top-notification');
      applyOffset(false);
    };
  }, [show, closing]);

  const handleClose = (e) => {
    e && e.stopPropagation();
    if (closing) return;
    setClosing(true);
    setTimeout(() => { onClose && onClose(); }, 200);
  };

  const shouldRender = show || closing;
  if (!shouldRender) return null;

  const contentDesktop = (
    <div ref={bannerRef} className={`Notification__wrapper NotificationDesktop__wrapper Notification__pos--${position}`}>
      <div className={`Notification Notification--${variant} ${!entered && !closing ? 'Notification--pre' : ''} ${closing ? 'Notification--closing' : ''}`} {...clickableProps}>
        {iconSrc && (
          <div className="Notification__icon">
            <img src={iconSrc} alt="" />
          </div>
        )}
        <div className="Notification__text">
          {title && <div className="Notification__title">{title}</div>}
          {(subtitle || (cta && cta.type === 'link' && cta.text)) && (
            <div className="Notification__subtitle">
              <span>{subtitle} </span>
              {cta && cta.type === 'link' && cta.href && (
                <a
                  href={cta.href}
                  className="Notification__link"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.assign(cta.href);
                  }}
                >
                  {cta.text}
                </a>
              )}
            </div>
          )}
        </div>
        {cta && cta.type === 'button' && (
          <button
            className="Notification__cta"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              cta.onClick && cta.onClick();
            }}
          >
            {cta.text}
          </button>
        )}
        {isClosable && (
          <div className="Notification__closeWrap">
            <button
              className="Notification__close"
              onClick={handleClose}
              aria-label="close"
            >
              <img src={closeIconSrc} alt="close" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Mobile variant commented out — replaced by top banner design
  // const contentMobile = (
  //   <div className={`NotificationMobile__wrapper`}>
  //     <div className={`NotificationMobile ${entered ? 'NotificationMobile--enter' : ''} ${closing ? 'NotificationMobile--closing' : ''}`} onClick={clickableProps.onClick}>
  //       {iconSrc && (
  //         <div className="NotificationMobile__icon">
  //           <img src={iconSrc} alt="" />
  //         </div>
  //       )}
  //       <div className="NotificationMobile__text">
  //         {title && <div className="NotificationMobile__title">{title}</div>}
  //         {subtitle && <div className="NotificationMobile__subtitle">{subtitle}</div>}
  //       </div>
  //       <div className="NotificationMobile__buttons">
  //         {cta && (
  //           <button
  //             className="NotificationMobile__cta"
  //             onClick={(e) => {
  //               e.preventDefault();
  //               e.stopPropagation();
  //               if (cta.type === 'button' && cta.onClick) return cta.onClick();
  //               if (cta.type === 'link' && cta.href) return window.location.assign(cta.href);
  //             }}
  //           >
  //             {cta.text}
  //           </button>
  //         )}
  //         {isClosable && (
  //           <button className="NotificationMobile__cta NotificationMobile__cta--secondary" onClick={handleClose}>
  //             {t('general.close') || 'Close'}
  //           </button>
  //         )}
  //       </div>
  //     </div>
  //   </div>
  // );

  return contentDesktop;
}


